import React, { useState, useEffect } from 'react';
import { Link as ReactRouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import {
  getStatesSummary,
  getState,
  createState,
  updateState,
  deleteState,
  getStateAuditLog,
} from '../../../services/fees.service';
import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { hasPermission, Permission } from '../../../models/role.model';
import { auditEntrySummary, auditEntryChanges } from './fees-audit-helpers';
import type { StateAuditEntry } from '../../../models/stateAudit.model';
import { State, StateSummary } from '../../../models/state.model';
import { County } from '../../../models/county.model';
import { Fee, FeeCondition } from '../../../models/fee.model';
import { FEE_PARAMETERS, getFeeParameter, describeConditions } from '../../../utils/fee-calculator.util';

interface CreateFeeForm {
  stateName: string;
  stateAbbr: string;
  feeName: string;
  feeType: 'fixed' | 'percentage';
  feeCost: string;
  feeDescription: string;
  feeConditions: FeeCondition[];
  feeRateUnit: 'percent' | 'per100';
  countyName: string;
}

// Wizard steps
const steps = ['Statewide Fees', 'Counties', 'County Fees', 'Review & Save'];

export default function FeesContainer() {
  const { currentUser } = useAppContext();
  const navigate = useNavigate();
  const { stateId } = useParams<{ stateId: string }>();
  const [feeActivityTab, setFeeActivityTab] = useState(0);
  const [auditLog, setAuditLog] = useState<StateAuditEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [states, setStates] = useState<StateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateDetailsLoading, setStateDetailsLoading] = useState(false);

  // Load states from API on component mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoading(true);
        const statesData = await getStatesSummary();
        setStates(statesData);
        setError(null);
      } catch (err) {
        console.error('Error loading states:', err);
        setError('Failed to load states. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadStates();
  }, []);

  const summarizeState = (state: State): StateSummary => ({
    id: state.id,
    name: state.name,
    abbreviation: state.abbreviation,
    feeCount: state.fees.length,
    countyCount: state.counties.length,
    createdAt: state.createdAt,
    updatedAt: state.updatedAt,
    isArchived: state.isArchived,
  });

  const openStateForEditing = async (stateId: string, onLoaded: (state: State) => void) => {
    try {
      setStateDetailsLoading(true);
      const fullState = await getState(stateId);
      onLoaded(fullState);
    } catch (err) {
      console.error('Error loading state details:', err);
      showSnackbar('Failed to load state details. Please try again.', 'error');
    } finally {
      setStateDetailsLoading(false);
    }
  };

  const [createStateForm, setCreateStateForm] = useState<CreateFeeForm>({
    stateName: '',
    stateAbbr: '',
    feeName: '',
    feeType: 'fixed',
    feeCost: '',
    feeDescription: '',
    feeConditions: [],
    feeRateUnit: 'percent',
    countyName: '',
  });

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [isWizardMode, setIsWizardMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const showActivityTabWithPermission = Boolean(
    isWizardMode && selectedState?.id && currentUser && hasPermission(currentUser, Permission.VIEW_FEE_LOGS),
  );

  useEffect(() => {
    if (!showActivityTabWithPermission || !selectedState?.id || feeActivityTab !== 1) return;
    let cancelled = false;
    setAuditLogLoading(true);
    getStateAuditLog(selectedState.id, { limit: 50 })
      .then((res) => {
        if (!cancelled) setAuditLog(res.items);
      })
      .catch((e) => {
        if (!cancelled) onError(e);
      })
      .finally(() => {
        if (!cancelled) setAuditLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedState?.id, showActivityTabWithPermission, feeActivityTab]);

  // Dialog states
  const [showAddStateDialog, setShowAddStateDialog] = useState(false);
  const [showAddFeeDialog, setShowAddFeeDialog] = useState(false);
  const [showAddCountyDialog, setShowAddCountyDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'state' | 'county' | 'fee';
    id: string;
    name: string;
  } | null>(null);

  // Editing states
  const [editingFee, setEditingFee] = useState<string | null>(null);
  const [editingCountyId, setEditingCountyId] = useState<string | null>(null);
  const [editingCountyFee, setEditingCountyFee] = useState<{ stateId: string; countyId: string; feeId: string } | null>(
    null,
  );

  // UI state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCreateStateChange =
    (field: keyof CreateFeeForm) => (event: React.ChangeEvent<HTMLInputElement> | { target: { value: unknown } }) => {
      setCreateStateForm({
        ...createStateForm,
        [field]: event.target.value,
      });
    };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveState = async () => {
    if (!selectedState) return;

    try {
      // Update the state in the backend with all the embedded data
      const updatedState = await updateState(selectedState.id, {
        name: selectedState.name,
        abbreviation: selectedState.abbreviation,
        fees: selectedState.fees,
        counties: selectedState.counties,
      });

      // Update the local states summary list with the saved data
      setStates(states.map((state) => (state.id === selectedState.id ? summarizeState(updatedState) : state)));

      // Update the selected state with the saved data
      setSelectedState(updatedState);

      showSnackbar('State configuration saved successfully!', 'success');

      // Reset the wizard
      handleReset();
    } catch (err) {
      console.error('Error saving state:', err);
      showSnackbar('Failed to save state configuration. Please try again.', 'error');
    }
  };

  const handleReset = () => {
    navigate('/fees');
    setActiveStep(0);
    setFeeActivityTab(0);
    setSelectedState(null);
    setIsWizardMode(false);
    setIsEditMode(false);
    setCreateStateForm({
      stateName: '',
      stateAbbr: '',
      feeName: '',
      feeType: 'fixed',
      feeCost: '',
      feeDescription: '',
      feeConditions: [],
      feeRateUnit: 'percent',
      countyName: '',
    });
  };

  // const startWizard = () => {
  //   setIsWizardMode(true);
  //   setActiveStep(0);
  // };

  // Each state is edited on its own page (/fees/:stateId); the page is opened by the route, not by local state
  const editState = (state: StateSummary) => {
    navigate(`/fees/${state.id}`);
  };

  useEffect(() => {
    if (!stateId) {
      setIsWizardMode(false);
      return;
    }
    if (selectedState?.id === stateId) {
      setIsWizardMode(true);
      return;
    }
    openStateForEditing(stateId, (fullState) => {
      setFeeActivityTab(0);
      setSelectedState(fullState);
      setIsEditMode(true);
      setIsWizardMode(true);
      setActiveStep(0); // Start at the beginning
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId]);

  const handleAddState = async () => {
    if (createStateForm.stateName && createStateForm.stateAbbr) {
      try {
        const newState = await createState({
          name: createStateForm.stateName,
          abbreviation: createStateForm.stateAbbr,
        });
        setStates([...states, summarizeState(newState)]);
        setSelectedState(newState);
        setCreateStateForm({
          stateName: '',
          stateAbbr: '',
          feeName: '',
          feeType: 'fixed',
          feeCost: '',
          feeDescription: '',
          feeConditions: [],
          feeRateUnit: 'percent',
          countyName: '',
        });
        setShowAddStateDialog(false);
        handleNext();
        showSnackbar('State added successfully!', 'success');
      } catch (err) {
        console.error('Error creating state:', err);
        showSnackbar('Failed to create state. Please try again.', 'error');
      }
    }
  };

  const handleAddFee = async (stateId: string, countyId?: string) => {
    if (createStateForm.feeName && createStateForm.feeCost) {
      try {
        // Create the fee object locally
        const newFee: Fee = {
          id: `fee_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: createStateForm.feeName,
          type: createStateForm.feeType,
          cost: createStateForm.feeCost,
          description: createStateForm.feeDescription,
          conditions: createStateForm.feeConditions.filter((c) => c.value.trim() !== ''),
          rateUnit: createStateForm.feeType === 'percentage' ? createStateForm.feeRateUnit : undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Update the selected state locally to include the new fee (persisted on Finish/Save)
        setSelectedState((prev) => {
          if (!prev || prev.id !== stateId) return prev;
          if (countyId) {
            // Add fee to specific county
            return {
              ...prev,
              counties: prev.counties.map((county) =>
                county.id === countyId ? { ...county, fees: [...county.fees, newFee] } : county,
              ),
            };
          }
          // Add fee to state
          return {
            ...prev,
            fees: [...prev.fees, newFee],
          };
        });

        setCreateStateForm({
          ...createStateForm,
          feeName: '',
          feeType: 'fixed',
          feeCost: '',
          feeDescription: '',
          feeConditions: [],
          feeRateUnit: 'percent',
        });
        setShowAddFeeDialog(false);
        setEditingCountyFee(null);
        showSnackbar('Fee added successfully!', 'success');
      } catch (err) {
        console.error('Error creating fee:', err);
        showSnackbar('Failed to create fee. Please try again.', 'error');
      }
    }
  };

  const handleEditCounty = (county: County) => {
    setEditingCountyId(county.id);
    setCreateStateForm({ ...createStateForm, countyName: county.name });
    setShowAddCountyDialog(true);
  };

  const handleCloseCountyDialog = () => {
    setShowAddCountyDialog(false);
    setEditingCountyId(null);
    setCreateStateForm({ ...createStateForm, countyName: '' });
  };

  const handleRenameCounty = (stateId: string, countyId: string) => {
    const name = createStateForm.countyName.trim();
    if (!name) return;

    // Persisted on Finish/Save
    setSelectedState((prev) =>
      prev && prev.id === stateId
        ? {
            ...prev,
            counties: prev.counties.map((c) =>
              c.id === countyId ? { ...c, name, updatedAt: new Date().toISOString() } : c,
            ),
          }
        : prev,
    );
    handleCloseCountyDialog();
    showSnackbar('County updated successfully!', 'success');
  };

  const handleAddCounty = async (stateId: string) => {
    if (createStateForm.countyName) {
      try {
        // Create the county object locally
        const newCounty: County = {
          id: `county_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: createStateForm.countyName,
          fees: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Update the selected state locally to include the new county (persisted on Finish/Save)
        setSelectedState((prev) =>
          prev && prev.id === stateId ? { ...prev, counties: [...(prev.counties || []), newCounty] } : prev,
        );

        setCreateStateForm({
          ...createStateForm,
          countyName: '',
        });
        setShowAddCountyDialog(false);
        showSnackbar('County added successfully!', 'success');
      } catch (err) {
        console.error('Error creating county:', err);
        showSnackbar('Failed to create county. Please try again.', 'error');
      }
    }
  };

  const handleEditFee = (stateId: string, feeId: string, countyId?: string) => {
    const state = selectedState?.id === stateId ? selectedState : undefined;
    let fee: Fee | undefined;

    if (countyId) {
      const county = state?.counties.find((c) => c.id === countyId);
      fee = county?.fees.find((f) => f.id === feeId);
    } else {
      fee = state?.fees.find((f) => f.id === feeId);
    }

    if (fee) {
      setCreateStateForm({
        ...createStateForm,
        feeName: fee.name,
        feeType: fee.type,
        feeCost: fee.cost,
        feeDescription: fee.description || '',
        feeConditions: fee.conditions ?? [],
      });

      // Clear previous editing states
      setEditingFee(null);
      setEditingCountyFee(null);

      if (countyId) {
        // Editing a county fee
        setEditingCountyFee({ stateId, countyId, feeId });
      } else {
        // Editing a state fee
        setEditingFee(feeId);
      }
      setShowAddFeeDialog(true);
    }
  };

  const handleSaveFee = (stateId: string, countyId?: string) => {
    if (createStateForm.feeName && createStateForm.feeCost) {
      setSelectedState((prev) => {
        if (!prev || prev.id !== stateId) return prev;
        if (countyId && editingCountyFee) {
          return {
            ...prev,
            counties: prev.counties.map((county) =>
              county.id === countyId
                ? {
                    ...county,
                    fees: county.fees.map((fee) =>
                      fee.id === editingCountyFee.feeId
                        ? {
                            ...fee,
                            name: createStateForm.feeName,
                            type: createStateForm.feeType,
                            cost: createStateForm.feeCost,
                            description: createStateForm.feeDescription,
                            conditions: createStateForm.feeConditions.filter((c) => c.value.trim() !== ''),
                            rateUnit: createStateForm.feeType === 'percentage' ? createStateForm.feeRateUnit : undefined,
                            updatedAt: new Date().toISOString(),
                          }
                        : fee,
                    ),
                  }
                : county,
            ),
          };
        } else if (editingFee) {
          return {
            ...prev,
            fees: prev.fees.map((fee) =>
              fee.id === editingFee
                ? {
                    ...fee,
                    name: createStateForm.feeName,
                    type: createStateForm.feeType,
                    cost: createStateForm.feeCost,
                    description: createStateForm.feeDescription,
                    conditions: createStateForm.feeConditions.filter((c) => c.value.trim() !== ''),
                    rateUnit: createStateForm.feeType === 'percentage' ? createStateForm.feeRateUnit : undefined,
                    updatedAt: new Date().toISOString(),
                  }
                : fee,
            ),
          };
        }
        return prev;
      });

      setCreateStateForm({
        ...createStateForm,
        feeName: '',
        feeType: 'fixed',
        feeCost: '',
        feeDescription: '',
        feeConditions: [],
        feeRateUnit: 'percent',
      });
      setEditingFee(null);
      setEditingCountyFee(null);
      setShowAddFeeDialog(false);
      showSnackbar('Fee updated successfully!', 'success');
    }
  };

  const handleDelete = (type: 'state' | 'county' | 'fee', id: string, name: string) => {
    setDeleteTarget({ type, id, name });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const { type, id } = deleteTarget;

    try {
      if (type === 'state') {
        await deleteState(id);
        setStates(states.filter((state) => state.id !== id));
        if (selectedState?.id === id) {
          setSelectedState(null);
        }
        showSnackbar('State deleted successfully!', 'success');
      } else if (type === 'county') {
        // Remove county from the currently selected state (persisted on Finish/Save)
        setSelectedState((prev) => (prev ? { ...prev, counties: prev.counties.filter((c) => c.id !== id) } : prev));
        showSnackbar('County deleted successfully!', 'success');
      } else if (type === 'fee') {
        // Remove fee from the currently selected state (persisted on Finish/Save)
        setSelectedState((prev) => {
          if (!prev) return prev;
          const county = prev.counties.find((c) => c.fees?.some((f) => f.id === id));
          if (county) {
            return {
              ...prev,
              counties: prev.counties.map((c) =>
                c.id === county.id ? { ...c, fees: c.fees.filter((f) => f.id !== id) } : c,
              ),
            };
          }
          return { ...prev, fees: prev.fees.filter((f) => f.id !== id) };
        });
        showSnackbar('Fee deleted successfully!', 'success');
      }

      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      showSnackbar('Failed to delete item. Please try again.', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderFeesTable = (fees: Fee[], stateId: string, countyId?: string) => (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, verticalAlign: 'middle' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Fee</TableCell>
            <TableCell width={108}>Type</TableCell>
            <TableCell width={120} align="right">
              Amount
            </TableCell>
            <TableCell width={96} align="right" sx={{ pr: 1.5 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fees.map((fee) => (
            <TableRow key={fee.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fee.name}
                </Typography>
                {fee.description ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {fee.description}
                  </Typography>
                ) : null}
                {fee.conditions?.length ? (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#0a2543' }}>
                    If {describeConditions(fee.conditions)}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>
                <Chip
                  label={fee.type === 'percentage' ? 'Percentage' : 'Fixed'}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 500, fontSize: '0.7rem' }}
                />
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                >
                  {fee.type === 'percentage' ? `${fee.cost}%` : `$${fee.cost}`}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ pr: 0.5, whiteSpace: 'nowrap' }}>
                <IconButton
                  size="small"
                  onClick={() => handleEditFee(stateId, fee.id, countyId)}
                  aria-label={`Edit ${fee.name}`}
                  sx={{ color: '#333', '&:hover': { backgroundColor: 'rgba(51, 51, 51, 0.08)' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete('fee', fee.id, fee.name)}
                  aria-label={`Delete ${fee.name}`}
                  sx={{ color: '#333', '&:hover': { backgroundColor: 'rgba(51, 51, 51, 0.08)' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCountiesTable = (counties: County[]) => (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, verticalAlign: 'middle' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>County</TableCell>
            <TableCell width={140} align="right">
              County fees
            </TableCell>
            <TableCell width={96} align="right" sx={{ pr: 1.5 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {counties.map((county) => (
            <TableRow key={county.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {county.name}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                >
                  {county.fees?.length ?? 0}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ pr: 0.5, whiteSpace: 'nowrap' }}>
                <IconButton
                  size="small"
                  onClick={() => handleEditCounty(county)}
                  aria-label={`Edit ${county.name}`}
                  sx={{ color: '#333', '&:hover': { backgroundColor: 'rgba(51, 51, 51, 0.08)' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete('county', county.id, county.name)}
                  aria-label={`Delete ${county.name}`}
                  sx={{ color: '#333', '&:hover': { backgroundColor: 'rgba(51, 51, 51, 0.08)' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Wizard Step Components
  const renderStepContent = (step: number) => {
    switch (step) {
      case -1:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, color: '#0a2543' }}>
              Select a State to Configure
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'normal' }}>
              Choose an existing state to edit, or create a new one to get started.
            </Typography>

            <Grid container spacing={2}>
              {states.map((state) => (
                <Grid key={state.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 3 },
                      border: '1px solid #e0e0e0',
                    }}
                    onClick={() => {
                      openStateForEditing(state.id, (fullState) => {
                        setSelectedState(fullState);
                        handleNext();
                      });
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#0a2543' }}>
                        {state.abbreviation} - {state.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'normal' }}>
                        {state.feeCount || 0} state fees • {state.countyCount || 0} counties
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<EditIcon />} sx={{ fontWeight: 500 }}>
                        Edit
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}

              {/* <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                  onClick={() => setShowAddStateDialog(true)}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <AddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Add New State
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'normal' }}>
                      Create a new state configuration
                    </Typography>
                  </CardContent>
                </Card>
              </Grid> */}
            </Grid>
          </Box>
        );

      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setShowAddFeeDialog(true);
                    setEditingFee(null);
                    setEditingCountyFee(null);
                  }}
                  sx={{ fontWeight: 500 }}
                >
                  Add Fee
                </Button>
              </Box>

              {(selectedState?.fees?.length || 0) === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontStyle: 'normal' }}
                >
                  No state fees configured yet. Add your first fee to get started.
                </Typography>
              ) : (
                renderFeesTable(selectedState?.fees || [], selectedState?.id || '')
              )}
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {selectedState?.abbreviation} Counties
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddCountyDialog(true)}
                    sx={{ fontWeight: 500 }}
                  >
                    Add County
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
              {(selectedState?.counties?.length || 0) === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontStyle: 'normal' }}
                >
                  No counties added yet. Add counties that have specific fee requirements.
                </Typography>
              ) : (
                renderCountiesTable(selectedState?.counties || [])
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <h3>County Fees</h3>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'normal' }}>
              Set up fees for each county in {selectedState?.abbreviation}.
            </Typography>

            {(selectedState?.counties || []).map((county) => (
              <Box key={county.id} sx={{ mb: 4 }}>
                <Grid container spacing={2} sx={{ mb: 1.5, alignItems: 'center' }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        bgcolor: 'transparent',
                      }}
                    >
                      {county.name} County
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setShowAddFeeDialog(true);
                          setEditingFee(null);
                          setEditingCountyFee({ stateId: selectedState?.id || '', countyId: county.id, feeId: '' });
                          // Reset form for new fee
                          setCreateStateForm({
                            ...createStateForm,
                            feeName: '',
                            feeType: 'fixed',
                            feeCost: '',
                            feeDescription: '',
                            feeConditions: [],
                            feeRateUnit: 'percent',
                          });
                        }}
                        sx={{ fontWeight: 500 }}
                      >
                        Add County Fee
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                {(county.fees?.length || 0) === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'center', py: 2, color: 'text.secondary', fontStyle: 'normal' }}
                  >
                    No fees configured for this county yet.
                  </Typography>
                ) : (
                  renderFeesTable(county.fees || [], selectedState?.id || '', county.id)
                )}
              </Box>
            ))}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ p: 3 }}>
            <h3>Review & Save</h3>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'normal' }}>
              Review your fee configuration for {selectedState?.abbreviation} before saving.
            </Typography>

            <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#0a2543' }}>
                  {selectedState?.abbreviation} - {selectedState?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'normal' }}>
                  {selectedState?.fees?.length || 0} state fees • {selectedState?.counties?.length || 0} counties
                </Typography>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total Configuration
              </Typography> */}
              <Chip label="Ready to Save" color="success" icon={<CheckIcon />} sx={{ fontWeight: 500 }} />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  // Main Dashboard View
  const renderDashboard = () => (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: 3 }}>
        {/* Header Section - Matching Service Forms style */}
        <div className="applications">
          <h2 className="pb-2 mt-4 mb-3 border-bottom">Fee Management</h2>
        </div>

        {/* Loading and Error States */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Quick Actions Toolbar */}
            <div className="pb-2 mt-4 mb-3">
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                <Button variant="contained" onClick={() => setShowAddStateDialog(true)}>
                  New State
                </Button>
              </Box>
            </div>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell>State Name</TableCell>
                    <TableCell align="center">Fees</TableCell>
                    <TableCell align="center">Counties</TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {states.map((state) => (
                    <TableRow key={state.id}>
                      <TableCell>{state.abbreviation}</TableCell>
                      <TableCell>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            editState(state);
                          }}
                        >
                          <span>{state.name}</span>
                        </a>
                      </TableCell>
                      <TableCell align="center">{state.feeCount || 0}</TableCell>
                      <TableCell align="center">{state.countyCount || 0}</TableCell>
                      <TableCell align="left">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Tooltip title="Delete">
                            <ReactRouterLink onClick={() => handleDelete('state', state.id, state.name)} to="">
                              <DeleteIcon color="error" fontSize="small" />
                            </ReactRouterLink>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </Box>
  );

  // Wizard View
  const renderWizard = () => (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleReset} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <h2>{isEditMode ? 'Edit State Configuration' : 'Configure Fees'}</h2>
        </Box>

        {showActivityTabWithPermission ? (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={feeActivityTab} onChange={(_e, v) => setFeeActivityTab(v)} aria-label="fee configuration tabs">
              <Tab label="Configuration" id="fee-tab-0" />
              <Tab label="Activity" id="fee-tab-1" />
            </Tabs>
          </Box>
        ) : null}

        <Box
          sx={{
            display: showActivityTabWithPermission && feeActivityTab === 1 ? 'none' : 'block',
          }}
        >
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ fontWeight: 500 }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{ fontWeight: 500 }}
            >
              Back
            </Button>
            <Box>
              <Button onClick={handleReset} sx={{ mr: 1, fontWeight: 500 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={activeStep === steps.length - 1 ? handleSaveState : handleNext}
                endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <ArrowForwardIcon />}
                sx={{ fontWeight: 500 }}
              >
                {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
              </Button>
            </Box>
          </Box>

          {renderStepContent(activeStep)}
        </Box>

        {showActivityTabWithPermission && feeActivityTab === 1 ? (
          <Box sx={{ pt: 1 }}>
            <Typography variant="h6" gutterBottom>
              Audit log
            </Typography>
            {auditLogLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : auditLog.length === 0 ? (
              <Typography color="text.secondary">No activity yet.</Typography>
            ) : (
              <List dense disablePadding>
                {auditLog.map((entry, idx) => (
                  <ListItem
                    key={`${entry.timestamp}-${idx}`}
                    alignItems="flex-start"
                    disableGutters
                    sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <ListItemText
                      primary={auditEntrySummary(entry)}
                      secondary={auditEntryChanges(entry)}
                      slotProps={{
                        primary: { variant: 'body2' },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        ) : null}
      </Paper>
    </Box>
  );

  return (
    <>
      {stateDetailsLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.6)',
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {isWizardMode ? renderWizard() : renderDashboard()}

      {/* Add State Dialog */}
      <Dialog open={showAddStateDialog} onClose={() => setShowAddStateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#0a2543' }}>Add New State</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="State Name"
                value={createStateForm.stateName}
                onChange={handleCreateStateChange('stateName')}
                required
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="State Abbreviation"
                value={createStateForm.stateAbbr}
                onChange={handleCreateStateChange('stateAbbr')}
                required
                slotProps={{ htmlInput: { maxLength: 2 } }}
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddStateDialog(false)} sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button onClick={handleAddState} variant="contained" sx={{ fontWeight: 500 }}>
            Add State
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Fee Dialog */}
      <Dialog open={showAddFeeDialog} onClose={() => setShowAddFeeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#0a2543' }}>
          {editingFee ? 'Edit Fee' : editingCountyFee ? 'Add County Fee' : 'Add State Fee'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Fee Name"
                value={createStateForm.feeName}
                onChange={handleCreateStateChange('feeName')}
                required
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontWeight: 500 }}>Fee Type</InputLabel>
                <Select
                  value={createStateForm.feeType}
                  label="Fee Type"
                  onChange={(e) => handleCreateStateChange('feeType')(e as any)}
                >
                  <MenuItem value="fixed">Fixed Amount ($)</MenuItem>
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Cost"
                value={createStateForm.feeCost}
                onChange={handleCreateStateChange('feeCost')}
                required
                type="number"
                slotProps={{
                  htmlInput: {
                    step: createStateForm.feeType === 'percentage' ? 0.01 : 0.01,
                    min: 0,
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {createStateForm.feeType === 'percentage' ? '%' : '$'}
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500 } }}
              />
            </Grid>
            {createStateForm.feeType === 'percentage' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 500 }}>Rate Applies To</InputLabel>
                  <Select
                    value={createStateForm.feeRateUnit}
                    label="Rate Applies To"
                    onChange={(e) =>
                      setCreateStateForm((prev) => ({ ...prev, feeRateUnit: e.target.value as 'percent' | 'per100' }))
                    }
                  >
                    <MenuItem value="percent">Percent of base price</MenuItem>
                    <MenuItem value="per100">Dollars per $100 of base price</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={createStateForm.feeDescription}
                onChange={handleCreateStateChange('feeDescription')}
                multiline
                rows={3}
                sx={{ '& .MuiInputLabel-root': { fontWeight: 500 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a2543', mb: 1 }}>
                Conditions (fee is added only if all match; leave empty to always add)
              </Typography>
              {createStateForm.feeConditions.map((condition, index) => {
                const param = getFeeParameter(condition.field);
                const update = (patch: Partial<FeeCondition>) =>
                  setCreateStateForm((prev) => ({
                    ...prev,
                    feeConditions: prev.feeConditions.map((c, i) => (i === index ? { ...c, ...patch } : c)),
                  }));
                return (
                  <Grid container spacing={1} key={index} sx={{ mb: 1, alignItems: "center" }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>If</InputLabel>
                        <Select
                          label="If"
                          value={condition.field}
                          onChange={(e) => {
                            const next = getFeeParameter(e.target.value);
                            update({
                              field: e.target.value,
                              value: '',
                              operator: condition.operator === '!=' ? '!=' : next?.kind === 'number' ? condition.operator : '=',
                            });
                          }}
                        >
                          {FEE_PARAMETERS.map((p) => (
                            <MenuItem key={p.field} value={p.field}>
                              {p.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 4, md: 2 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={condition.operator}
                          onChange={(e) => update({ operator: e.target.value as FeeCondition['operator'] })}
                        >
                          <MenuItem value="=">{param?.multiple ? 'is any of' : '='}</MenuItem>
                          <MenuItem value="!=">{param?.multiple ? 'is none of' : '!='}</MenuItem>
                          {param?.kind === 'number' && <MenuItem value="<=">&lt;=</MenuItem>}
                          {param?.kind === 'number' && <MenuItem value=">=">&gt;=</MenuItem>}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 8, md: 5 }}>
                      {param?.kind === 'select' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            displayEmpty
                            multiple={param.multiple}
                            value={param.multiple ? condition.value.split(',').filter(Boolean) : condition.value}
                            renderValue={(v) => (Array.isArray(v) ? v.join(', ') : v)}
                            onChange={(e) =>
                              update({
                                value: Array.isArray(e.target.value) ? e.target.value.join(',') : e.target.value,
                              })
                            }
                          >
                            {param.options?.map((o) => (
                              <MenuItem key={o} value={o}>
                                {o}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={param?.field === 'city' ? 'City (comma-separate several)' : 'Value'}
                          value={condition.value}
                          onChange={(e) => update({ value: e.target.value })}
                        />
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setCreateStateForm((prev) => ({
                            ...prev,
                            feeConditions: prev.feeConditions.filter((_, i) => i !== index),
                          }))
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setCreateStateForm((prev) => ({
                    ...prev,
                    feeConditions: [
                      ...prev.feeConditions,
                      { field: FEE_PARAMETERS[0].field, operator: '=', value: '' },
                    ],
                  }))
                }
              >
                Add condition
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddFeeDialog(false)} sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (editingFee) {
                handleSaveFee(selectedState?.id || '');
              } else if (editingCountyFee) {
                // For county fees, check if it's a new fee (empty feeId) or editing existing
                if (editingCountyFee.feeId === '') {
                  handleAddFee(editingCountyFee.stateId, editingCountyFee.countyId);
                } else {
                  handleSaveFee(editingCountyFee.stateId, editingCountyFee.countyId);
                }
              } else {
                handleAddFee(selectedState?.id || '');
              }
            }}
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            {editingFee ? 'Save Changes' : 'Add Fee'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit County Dialog */}
      <Dialog open={showAddCountyDialog} onClose={handleCloseCountyDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#0a2543' }}>
          {editingCountyId ? 'Edit County' : 'Add New County'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="County Name"
            value={createStateForm.countyName}
            onChange={handleCreateStateChange('countyName')}
            required
            sx={{ mt: 1, '& .MuiInputLabel-root': { fontWeight: 500 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCountyDialog} sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              editingCountyId
                ? handleRenameCounty(selectedState?.id || '', editingCountyId)
                : handleAddCounty(selectedState?.id || '')
            }
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            {editingCountyId ? 'Save Changes' : 'Add County'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle sx={{ fontWeight: 600, color: '#0a2543' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontStyle: 'normal' }}>
            Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{ backgroundColor: '#333', fontWeight: 500, '&:hover': { backgroundColor: '#555' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
